import * as Highcharts from 'highcharts';
import 'highcharts/modules/pattern-fill';
import 'highcharts/modules/exporting';
import 'highcharts/modules/export-data';
import 'highcharts/modules/offline-exporting';
import '../../highcharts-options';

import getUnicodeFlagIcon from 'country-flag-icons/unicode'

import { getChartById, getKeyTime, colStat, getUnits } from '../../../utils';
import { update as evalUpdate, callbackGlobStat as evalCallbackGlobStat } from '../../../modules/evaluation';

import { Aeroval } from '../../../types/global';
import { RegStatData } from '../../../types/data';
declare var aeroval: Aeroval;

export function plotRegionalStatisticsSingleModel(data: RegStatData, statisticDir: string, parameterDir: string, modelDir: string, modelVar: string, updateTS: boolean, updateScat: boolean, id = 'regStat', chartOptions: any = {}): void {
	//decompose statistics: support obs/mod_trends
	if (statisticDir.includes('/')) {
		var firstUnderscore = statisticDir.indexOf('_');
		var stat1 = `${statisticDir.split('/')[0]}_${statisticDir.slice(firstUnderscore + 1)}`;
		var stat2 = statisticDir.split('/')[1];
		var stats = [stat1, stat2];
	} else {
		var stats = [statisticDir];
	}

	const statistic = aeroval.statistics[statisticDir];

	//time selection
	const keyTime = getKeyTime(aeroval.time, aeroval.season);

	//select right parameter, layer and model
	const modData = data[modelDir][modelVar];

	var series = [] as any;
	for (let stat of stats) {
		var seriesData = [];
		var regionalStatistics = {} as any;
		var x = 0;

		for (let region in aeroval.regions) {
			if (region != '' && modData?.[region]?.[keyTime]) {
				const isSelectedRegion = region == aeroval.region || region == aeroval.station
				const statData = modData[region][keyTime][stat];
				if (statData && typeof statData === 'object') {
					const mapVar = statData.map_var;
					var value = statData[mapVar] as number;
				} else if (stat === 'nrms') {
					var value = (modData[region][keyTime]['rms'] as number) / (modData[region][keyTime]['refdata_mean'] as number);
				} else {
					var value = statData as number;
				}

				if (aeroval.settings.showEmptyRegions === 'false' && (value === null || value === undefined)) continue

				if (statistic.unit === '%' && !stat.includes('trends')) {
					value *= 100;
				}

				regionalStatistics[region] = {
					statistic: value,
					nStations: modData[region][keyTime]['num_coords_with_data'],
					nPoints: modData[region][keyTime]['num_valid']
				};

				// set up color
				if (stats.length > 1 && stat.includes('mod')) {
					var color = '#fdaa56';
					var borderColor = '#ff9933';
				} else {
					if (aeroval.settings.regionalStatisticsSingleModelColor === 'theme') {
						color = '#d3d3d3';
						borderColor = '#808080';
					} else {
						//prepare the legend
						if (statistic.scale == null) {
							var scale = aeroval.ranges[parameterDir].scale;
						} else {
							if (statistic.unit == 'var') {
								// this is a relative scale: multiply by Max of Mean scale
								var statScale = aeroval.ranges[parameterDir].scale;
								scale = statistic.scale.map((x) => x * Math.max(...statScale));
								// round down to two decimals
								scale = scale.map((x) => parseFloat(x.toFixed(2))) as number[];
							} else {
								// set scale as it is
								scale = statistic.scale;
							}
						}
						color = colStat(value, scale, statisticDir);
						borderColor = color;
					}
				}

				if (aeroval.globalRegions.includes(region)) {
					var colorStyle = {
						pattern: {
							path: {
								d: 'M 0 0 L 10 10 M 9 -1 L 11 1 M -1 9 L 1 11',
							},
							strokeWidth: 3,
							width: 10,
							height: 10,
							color,
							opacity: 1,
						},
					} as any;
					var borderWidth = isSelectedRegion ? 2 : 1;
					//var borderColor = aeroval.settings.theme == 'light' ? 'black' : 'white'
				} else {
					colorStyle = color;
					borderWidth = isSelectedRegion ? 2 : 0;
					borderColor = aeroval.settings.theme == 'light' ? 'black' : 'white'
				}

				var label: string | null = null;
				const showCountryFlags = aeroval.cfg.webdisp_opts.country_flag || chartOptions.countryFlags
				if (showCountryFlags && !aeroval.globalRegions.includes(region)) {
					var country = region;
					if (country === 'Macedonia') {
						country = 'North Macedonia';
					}
					if (country === 'Holy See (Vatican City State)') {
						country = 'Vatican';
					}

					label = getUnicodeFlagIcon(country)
				}

				seriesData.push({
					x,
					y: regionalStatistics[region]['statistic'],
					label,
					color: colorStyle,
					stdv: 0,
					nStations: regionalStatistics[region]['nStations'],
					nPoints: regionalStatistics[region]['nPoints'],
					borderWidth,
					borderColor,
					name: `${stat}-${region}`,
				});
			} else {
				regionalStatistics[region] = {
					statistic: null,
					nStations: null,
					nPoints: null
				};
			}
			x++;
		}

		series.push({
			name: 'Average',
			type: 'column',
			yAxis: 0,
			data: seriesData,
			showInLegend: false,
			enableMouseTracking: true,
			tooltip: {
				enabled: true,
			},
			dataLabels: {
				enabled: true,
				allowOverlap: true,
				style: { fontSize: 8, fontWeight: 'bolder' },
				formatter: function (): string {
					return (this as any).point.label;
				},
				inside: false,
				y: 0,
				x: 0,
			},
		});
	}

	const unit = getUnits(statisticDir, parameterDir);
	var titleText = getTitle(parameterDir);
	var subtitleText = `${aeroval.frequency} data - all stations`
	const statisticName = statistic.name

	if (chartOptions.title?.enabled == false) {
		titleText = ''
		subtitleText = ''
	}

	const legendEnabled = chartOptions.legend && chartOptions.legend.hasOwnProperty('enabled') ? chartOptions.legend.enabled : false

	Highcharts.chart({
		chart: {
			renderTo: id,
			type: 'column',
			style: {
				cursor: 'pointer',
			},
			height: chartOptions.height ? chartOptions.height : undefined
		},
		title: {
			text: titleText,
		},
		subtitle: {
			text: subtitleText,
		},
		legend: {
			enabled: legendEnabled
		},
		xAxis: {
			categories: Object.keys(regionalStatistics),
			crosshair: true,
			labels: {
				overflow: 'justify',
				rotation: -45,
			},
			cursor: 'pointer',
		} as Highcharts.XAxisOptions,
		yAxis: [
			{
				title: {
					text: `${statisticName} ${unit}`,
				},
			},
			{
				gridLineWidth: 0,
				title: {
					text: 'Nb. of Stations (◆)',
				},
				opposite: true,
				visible: false,
			},
		],
		plotOptions: {
			column: {
				borderWidth: 0,
				shadow: false,
			},
			series: {
				cursor: 'pointer',
				point: {
					events: {
						mouseOver: function () {
							if (aeroval.settings.presentationMode !== 'true') {
								const thisPoint = this as any;
								const mapRegion = getChartById('map')?.get(thisPoint.category.name) as Highcharts.Series;
								const mapRegionName = mapRegion.userOptions.id
								if (mapRegionName == aeroval.region) return
								const hoverLoaderRegionalChart = document.querySelector('#hoverLoaderReg');
								if (hoverLoaderRegionalChart instanceof HTMLElement) {
									hoverLoaderRegionalChart.classList.remove('hide');
									hoverLoaderRegionalChart.classList.add('show');
								}
								aeroval.wait = false;
								aeroval.timeOver = setTimeout(function () {
									if (aeroval.wait === false && mapRegion) {
										mapRegion.show();
										evalUpdate('region', thisPoint.category.name);
										const hoverLoaderRegionalChart = document.querySelector('#hoverLoaderReg');
										if (hoverLoaderRegionalChart instanceof HTMLElement) {
											hoverLoaderRegionalChart.classList.remove('show');
											hoverLoaderRegionalChart.classList.add('hide');
										}
									}
								}, 1000);
							}
						},
						mouseOut: function () {
							const thisPoint = (this as any).point;
							aeroval.wait = true;
							clearTimeout(aeroval.timeOver);
							const hoverLoaderRegionalChart = document.querySelector('#hoverLoaderReg');
							if (hoverLoaderRegionalChart instanceof HTMLElement) {
								hoverLoaderRegionalChart.classList.remove('show');
								hoverLoaderRegionalChart.classList.add('hide');
							}
							const mapRegion = getChartById('map')?.get(thisPoint.category.name);
							if (mapRegion instanceof Highcharts.Series) {
								mapRegion.hide();
							}
						},
						click: function () {
							console.log('click')
							const thisPoint = (this as any).point;
							aeroval.wait = false;
							clearTimeout(aeroval.timeOver);
							const mapRegion = getChartById('map')?.get(thisPoint.category.name);
							if (mapRegion instanceof Highcharts.Series) {
								mapRegion.show();
							}
							evalUpdate('region', thisPoint.category.name);
							const hoverLoaderRegionalChart = document.querySelector('#hoverLoaderReg');
							if (hoverLoaderRegionalChart instanceof HTMLElement) {
								hoverLoaderRegionalChart.classList.remove('show');
								hoverLoaderRegionalChart.classList.add('hide');
							}
						},
					},
				},
			},
		},
		tooltip: {
			shared: true,
			formatter: function () {
				const thisPoint = (this as any).point;
				if (!thisPoint) return '';
				var value: number | undefined = undefined;
				value = thisPoint.y;
				/*if (this.points?.[0] && this.points?.[1]) {
					value = (this.points[0].point.y as number) / (this.points[1].point.y as number);
				} else if (this.points?.[0]) {
					value = this.points[0].point.y;
				}*/
				if (this.category && statisticName) {
					var tooltip = `<b>${this.category}</b>`
					if (value) {
						tooltip += `<br>${statisticName}: <b>${value.toFixed(statistic.decimals)}</b>`
					}
					tooltip += `<br> Nb. of  Stations: <b>${thisPoint.nStations}</b>`;
					tooltip += `<br> Nb. of  Points: <b>${thisPoint.nPoints}</b>`;

					return tooltip
				} else {
					return ''
				}
			},
		},
		series,
	},
		function () {
			// when the chart is rendered
			var h = `<table class='table table-hover table-condensed slideTable'>`;
			h += '<thead>';
			h += '<tr>';
			h += "<th rowspan='1'>Region</th>";
			h += "<th rowspan='1'>Nb. of Stations</th>";
			h += "<th rowspan='1'>Nb. of Points</th>";
			h += `<th seriesDatapan='1'>${statisticName}</th>`;
			h += '</tr>';
			h += '</thead>';
			h += '<tbody>';
			for (let region in regionalStatistics) {
				h += '<tr>';
				h += `<td>${region}</td>`;
				h += `<td>${regionalStatistics[region]['nStations']}</td>`;
				h += `<td>${regionalStatistics[region]['nPoints']}</td>`;
				if (regionalStatistics[region]['statistic'] != null) {
					h += `<td>${regionalStatistics[region]['statistic'].toFixed(2)}</td>`;
				} else {
					h += `<td>${regionalStatistics[region]['statistic']}</td>`;
				}
				h += '</tr>';
			}
			h += '</tbody>';
			h += '</table>';

			const regStatTable = document.querySelector('#regStatTable');
			if (regStatTable instanceof HTMLElement) {
				regStatTable.innerHTML = h;
			}

			//toggleRegionalStatisticsSingleModelColor(aeroval.settings.regionalStatisticsSingleModelColor);
			evalCallbackGlobStat(updateTS, updateScat);
		}
	);
}

export function toggleRegionalStatisticsSingleModelColor(value: string): void {
	enableMode(value, 'regStat');
	aeroval.settings.regionalStatisticsSingleModelColor = value;
}

export function enableMode(value: string, id: string): void {
	if (!aeroval.ranges) return;

	const parameterDir = aeroval.parameter.dir;
	const statisticDir = aeroval.statistic.dir;
	if (!parameterDir || !statisticDir) return;

	const statistic = aeroval.statistics[statisticDir];

	if (value === 'theme') {
		const chart = getChartById(id);
		if (chart) {
			for (let point of chart.series[0].data) {
				var color = '#d3d3d3';
				if (aeroval.globalRegions.includes((point.category as any).name)) {
					var colorStyle = {
						pattern: {
							path: {
								d: 'M 0 0 L 10 10 M 9 -1 L 11 1 M -1 9 L 1 11',
							},
							strokeWidth: 3,
							width: 10,
							height: 10,
							color,
							opacity: 1,
						},
					} as any;
				} else {
					var colorStyle = color as any;
				}
				point.update(
					{
						color: colorStyle,
					} as any,
					false
				);
			}
			chart.redraw();
		}
	}
	if (value === 'value') {
		//prepare the legend
		if (statistic.scale == null) {
			var scale = aeroval.ranges[parameterDir].scale;
		} else {
			if (statistic.unit == 'var') {
				// this is a relative scale: multiply by Max of Mean scale
				var statScale = aeroval.ranges[parameterDir].scale;
				var scale = statistic.scale.map((x) => x * Math.max(...statScale));
				// round down to two decimals
				var scale = scale.map((x) => parseFloat(x.toFixed(2))) as number[];
			} else {
				// set scale as it is
				var scale = statistic.scale;
			}
		}

		if (!scale) return;
		const chart = getChartById(id);
		if (chart) {
			for (let point of chart.series[0].data) {
				var color = colStat((point as any).y, scale, statisticDir);
				if (aeroval.globalRegions.includes((point.category as any).name)) {
					var colorStyle = {
						pattern: {
							path: {
								d: 'M 0 0 L 10 10 M 9 -1 L 11 1 M -1 9 L 1 11',
							},
							strokeWidth: 3,
							width: 10,
							height: 10,
							color,
							opacity: 1,
						},
					} as any;
				} else {
					var colorStyle = color as any;
				}
				point.update(
					{
						color: colorStyle,
					} as any,
					false
				);
			}
			chart.redraw();
		}
	}
	aeroval.settings.regionalStatisticsColor = value;
}

function getTitle(parameterDir: string): string {
	const parameterName = aeroval.menu[parameterDir].name;
	var title = parameterName;
	if (aeroval.layer != 'Surface' && aeroval.layer != 'Column') {
		title += ` (${aeroval.layer})`;
	}
	title += ` - ${aeroval.time}`;
	if (aeroval.season != 'All') {
		title += ` (${aeroval.season})`;
	}
	return title;
}
